import { getInterfaceName, getPrimaryKey } from "EntityExtensions";
import type { Entity } from "breeze-client";

type OnEntityFetched = (entity: Entity) => void;
const entityFetchedSubscribers: Map<string, Set<OnEntityFetched>> = new Map();

export function subscribeToEntityFetched(
  entityInterfaceName: string,
  entityKey: string,
  onEntityFetched: OnEntityFetched,
): void {
  const subscriberKey = getSubscriberKey(entityInterfaceName, entityKey);
  if (!entityFetchedSubscribers.has(subscriberKey)) {
    entityFetchedSubscribers.set(subscriberKey, new Set());
  }
  entityFetchedSubscribers.get(subscriberKey)!.add(onEntityFetched);
}

export function unsubscribeFromEntityFetched(
  entityInterfaceName: string,
  entityKey: string,
  onEntityFetched: OnEntityFetched,
): void {
  const subscriberKey = getSubscriberKey(entityInterfaceName, entityKey);
  const subscribers = entityFetchedSubscribers.get(subscriberKey);
  if (subscribers) {
    subscribers.delete(onEntityFetched);
    if (subscribers.size === 0) {
      entityFetchedSubscribers.delete(subscriberKey);
    }
  }
}

/**
 * @package
 * Notify when Entity was attached as a result of a query.
 * To be invoked from EntityManager's "entityChanged" event when entityAction is AttachOnQuery.
 * NOTE: This function should not be used from any other module (outside Breeze)
 * */
export function notifyEntityFetched(entity: Entity): void {
  const entityInterfaceName = getInterfaceName(entity);
  const entityKey = getPrimaryKey(entity);
  const subscriberKey = getSubscriberKey(entityInterfaceName, entityKey);
  const subscribers = entityFetchedSubscribers.get(subscriberKey);
  if (subscribers) {
    for (const subscriber of subscribers) {
      subscriber(entity);
      subscribers.delete(subscriber);
      if (subscribers.size === 0) {
        entityFetchedSubscribers.delete(subscriberKey);
      }
    }
  }
}

function getSubscriberKey(entityInterfaceName: string, entityKey: string): string {
  return `${entityInterfaceName}|${entityKey}`;
}
